import {
	useFetchAccountPreviews,
	useFetchBondPreviews,
	useFetchBondRequestPreviews,
} from "@portal/api/previews";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useRecentlyViewedEntities } from "@portal/hooks/useRecentlyViewed";
import { useMemo } from "react";
import { match } from "ts-pattern";
import { fuzzyFilterOptions } from "../../../utils/search";
import {
	AccountSearchEntity,
	ActiveFilter,
	ActiveFilterEnum,
	BondRequestSearchEntity,
	BondSearchEntity,
	GlobalSearchEntity,
} from "./types";

export type GlobalSearchParams = {
	searchTerm: string;
	activeFilter: ActiveFilter;
	showClosed: boolean;
};

export type GlobalSearchPreviewResults = {
	accounts?: AccountSearchEntity[];
	bondRequests?: BondRequestSearchEntity[];
	bonds?: BondSearchEntity[];
	recentlyViewed?: GlobalSearchEntity[];
};

export const useGlobalSearchData = ({
	searchTerm,
	activeFilter,
	showClosed,
}: GlobalSearchParams) => {
	const { recentlyViewedEntities } = useRecentlyViewedEntities();
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const {
		accountPreviews,
		accountPreviewsIsLoading,
		accountPreviewsError,
		refetchAccountPreviews,
	} = useFetchAccountPreviews();
	const {
		bondRequestPreviews,
		bondRequestPreviewsIsLoading,
		bondRequestPreviewsError,
		refetchBondRequestPreviews,
	} = useFetchBondRequestPreviews();
	const { bondPreviews, bondPreviewsIsLoading, bondPreviewsError, refetchBondPreviews } =
		useFetchBondPreviews();

	const results: GlobalSearchPreviewResults = useMemo(() => {
		// If we're still loading any of the previews, we can return an empty array to avoid showing stale data
		if (accountPreviewsIsLoading || bondRequestPreviewsIsLoading || bondPreviewsIsLoading) {
			return {};
		}

		// If there's an error fetching previews and we don't have any data to show, log the error and return an empty array
		if (
			(!accountPreviews && accountPreviewsError) ||
			(!bondRequestPreviews && bondRequestPreviewsError) ||
			(!bondPreviews && bondPreviewsError)
		) {
			console.error("Error fetching previews", {
				accountPreviewsError,
				bondRequestPreviewsError,
				bondPreviewsError,
			});
			return {};
		}

		// If there's a global search term, filter the previews based on the term and active filter
		if (activeFilter === ActiveFilterEnum.ALL && !searchTerm) {
			if (recentlyViewedEntities.length === 0) {
				return {};
			} else {
				const recentlyViewedPreviews = recentlyViewedEntities.reduce<GlobalSearchEntity[]>(
					(acc, recentItem) => {
						const entity = match(recentItem)
							.with({ entityType: "account" }, ({ id }) => {
								const account = accountPreviews?.find((preview) => preview.id === id);

								return account
									? {
											relationType: "account" as const,
											entity: account,
										}
									: undefined;
							})
							.with({ entityType: "bondRequest" }, ({ id }) => {
								const bondRequest = bondRequestPreviews?.find((preview) => preview.id === id);

								return bondRequest
									? {
											relationType: "bondRequest" as const,
											entity: bondRequest,
										}
									: undefined;
							})
							.with({ entityType: "bond" }, ({ id }) => {
								const bond = bondPreviews?.find((preview) => preview.id === id);

								return bond
									? {
											relationType: "bond" as const,
											entity: bond,
										}
									: undefined;
							})
							.exhaustive();

						if (entity) {
							acc.push(entity);
						}

						return acc;
					},
					[],
				);

				return {
					recentlyViewed: recentlyViewedPreviews,
				};
			}
		} else {
			const filteredAccounts =
				(activeFilter === ActiveFilterEnum.ALL || activeFilter === ActiveFilterEnum.ACCOUNTS) &&
				accountPreviews
					? fuzzyFilterOptions(
							accountPreviews
								.filter((preview) => showClosed || preview.status !== "closed")
								.map((preview) => ({
									...preview,
									// The search values in order of importance for ranking
									searchValues: [
										preview.displayName,
										...preview.companies.map((c) => c.name),
									].filter((v): v is string => v != null),
								})),
							searchTerm || undefined,
							{ matchToAcronyms: true },
						).map(({ searchValues, ...preview }) => ({
							entity: preview,
							relationType: "account" as const,
						}))
					: [];

			const filteredBondRequests =
				(activeFilter === ActiveFilterEnum.ALL || activeFilter === ActiveFilterEnum.REQUESTS) &&
				bondRequestPreviews
					? fuzzyFilterOptions(
							bondRequestPreviews
								.filter((preview) => showClosed || preview.status !== "closed")
								.map((preview) => ({
									...preview,
									// The search values in order of importance for ranking
									searchValues: [
										preview.accountName,
										preview.principalName,
										isMinimalUIEnabled ? preview.name : preview.description,
										preview.amount?.toString(),
									].filter((v): v is string => v != null),
								})),
							searchTerm || undefined,
							{ matchToAcronyms: true },
						).map(({ searchValues, ...preview }) => ({
							entity: preview,
							relationType: "bondRequest" as const,
						}))
					: [];

			const filteredBonds =
				(activeFilter === ActiveFilterEnum.ALL || activeFilter === ActiveFilterEnum.BONDS) &&
				bondPreviews
					? fuzzyFilterOptions(
							bondPreviews
								.filter((preview) => showClosed || preview.status !== "closed")
								.map((preview) => ({
									...preview,
									// The search values in order of importance for ranking
									searchValues: [
										preview.number,
										preview.accountName,
										preview.principalName,
										isMinimalUIEnabled ? preview.name : preview.description,
										preview.amount.toString(),
										preview.obligeeName,
									].filter((v): v is string => v != null),
								})),
							searchTerm || undefined,
							{ matchToAcronyms: true },
						).map(({ searchValues, ...preview }) => ({
							entity: preview,
							relationType: "bond" as const,
						}))
					: [];

			return {
				accounts: filteredAccounts,
				bondRequests: filteredBondRequests,
				bonds: filteredBonds,
			};
		}
	}, [
		searchTerm,
		activeFilter,
		showClosed,
		recentlyViewedEntities,
		accountPreviews,
		accountPreviewsIsLoading,
		accountPreviewsError,
		bondRequestPreviews,
		bondRequestPreviewsIsLoading,
		bondRequestPreviewsError,
		bondPreviews,
		bondPreviewsIsLoading,
		bondPreviewsError,
		isMinimalUIEnabled,
	]);

	return {
		results,
		isLoading: accountPreviewsIsLoading || bondRequestPreviewsIsLoading || bondPreviewsIsLoading,
		error: accountPreviewsError || bondRequestPreviewsError || bondPreviewsError,
		refetchPreviews: () => {
			refetchAccountPreviews();
			refetchBondRequestPreviews();
			refetchBondPreviews();
		},
	};
};
